/**
 * Module: screens/creator/steps/ConsentStep
 * Role: Wizard Step 3/8 — 152-ФЗ personal data consent + оферта acceptance
 *       (FTR-1106 + FTR-1131 chrome rewrite, design Eqq6AbBPpm1zPPyErlkgJQ
 *       chat16). Two checkboxes, both required → combined `consent=true`
 *       persisted in store (backend keeps the single `consent` boolean).
 *       No openExternal(BOT_LINK) — ADR-022 §7.
 *
 * Uses:
 *   - react-router-dom:useNavigate
 *   - screens/creator/store:useWizardStore
 *   - screens/creator/_chrome/WizardShell
 *   - screens/flow/atoms/HintBar
 *   - styles/tokens:P
 *
 * Used by: CreatorOnboardingScreen.tsx
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWizardStore } from '../store';
import { HintBar } from '../../flow/atoms/HintBar';
import { P, T } from '../../../styles/tokens';
import {
  WizardBody, WizardCtaBar, WizardFrame, WizardHeader, WizardStepStrip,
} from '../_chrome/WizardShell';

function ConsentRow({
  checked, onToggle, children,
}: {
  checked: boolean; onToggle(): void; children: React.ReactNode;
}) {
  return (
    <label
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer',
      }}
    >
      <div
        style={{
          width: 22, height: 22, minWidth: 22, borderRadius: 6,
          border: `2px solid ${checked ? P.terracotta : P.line}`,
          background: checked ? P.terracotta : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.15s, border-color 0.15s', marginTop: 1,
        }}
      >
        {checked && (
          <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
            <path
              d="M1 5L4.5 8.5L11 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
      <span style={{ font: T.subhead, color: P.ink, lineHeight: 1.4 }}>
        {children}
      </span>
    </label>
  );
}

export default function ConsentStep() {
  const navigate = useNavigate();
  const { draft, setConsent } = useWizardStore();
  const [pd, setPd] = useState(draft.consent);
  const [offer, setOffer] = useState(draft.consent);
  const [error, setError] = useState<string | null>(null);
  const both = pd && offer;

  const handleBack = () => navigate(-1);

  function handleNext() {
    if (!both) {
      setError('Подтвердите оба согласия, чтобы продолжить');
      return;
    }
    setConsent(true);
    navigate('/creator-onboarding/categories');
  }

  return (
    <WizardFrame>
      <WizardHeader title="Верификация" onBack={handleBack} />
      <WizardStepStrip current={3} total={8} />
      <WizardBody>
        <div style={{ paddingTop: 10 }}>
          <div style={{ font: T.title2, color: P.ink, marginBottom: 4 }}>
            Согласия
          </div>
          <div style={{ font: T.footnote, color: P.inkSoft }}>
            Нужны для участия в кампаниях и верификации
          </div>
        </div>

        <div
          style={{
            borderRadius: 14, border: `1px solid ${P.line}`, background: P.card,
            padding: 16, font: T.footnote, color: P.inkSoft, lineHeight: 1.55,
          }}
        >
          Мы обрабатываем фото и ссылки на соцсети, чтобы убедиться, что заявку
          подаёт реальный человек, а не бот. Личность не устанавливаем,
          биометрические шаблоны не создаём. Селфи храним 90 дней. Отозвать
          согласие можно в любой момент через поддержку.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ConsentRow
            checked={pd}
            onToggle={() => { setError(null); setPd((v) => !v); }}
          >
            Я даю согласие на обработку персональных данных в соответствии с
            152-ФЗ
          </ConsentRow>
          <ConsentRow
            checked={offer}
            onToggle={() => { setError(null); setOffer((v) => !v); }}
          >
            Я принимаю условия{' '}
            <span style={{ color: P.terraDark, fontWeight: 600 }}>оферты</span>
            {' '}и{' '}
            <span style={{ color: P.terraDark, fontWeight: 600 }}>
              политики конфиденциальности
            </span>
          </ConsentRow>
        </div>

        {error && <HintBar>{error}</HintBar>}
      </WizardBody>
      <WizardCtaBar label="Далее" onClick={handleNext} disabled={!both} />
    </WizardFrame>
  );
}
